import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { detectDocumentEdges, refineCornerSubpixel } from "../../../utils/edgeDetection";

const DETECT_WIDTH = 320; // Downsampled width for edge detection
const DETECT_INTERVAL = 110; // ~9 fps: optimal balance of responsiveness and battery efficiency
const DETECT_INTERVAL_IDLE = 220; // ~4.5 fps when no document found for battery saving
const STABILITY_REQUIRED_FRAMES = 14; // 14 frames at 110ms = ~1.54s of steady hold
const COOLDOWN_MS = 2800; // ~2.8s cooldown after capture to give user time to position next page
const IDLE_SLOWDOWN_AFTER = 3000; // Slow detection after 3s of no document found

/**
 * Camera view with live edge detection overlay, Adobe Scan-style steady countdown,
 * and calibrated auto-capture cooldown.
 *
 * FIX: SVG overlay now precisely tracks the video display area even with
 * object-fit:contain letterboxing. Coordinates are computed from the actual
 * rendered video rectangle, not the container dimensions.
 *
 * Exposes a `capture()` method via ref for manual capture trigger.
 */
const CameraView = forwardRef(
  ({ stream, onCapture, autoCapture, isProcessing, phase = "camera" }, ref) => {
    const videoRef = useRef(null);
    const detectCanvasRef = useRef(null);
    const cornerHistoryRef = useRef([]);
    const trackedCornersRef = useRef(null);
    const lastCapturedCornersRef = useRef(null);
    const autoCapturedRef = useRef(false);
    const cooldownUntilRef = useRef(0);
    const missedFramesRef = useRef(0);
    const lastDocumentSeenRef = useRef(Date.now());

    const [corners, setCorners] = useState(null);
    const [stability, setStability] = useState(0);
    const [statusMessage, setStatusMessage] = useState("Point camera at document");
    const [inCooldown, setInCooldown] = useState(true);
    const [isFlashing, setIsFlashing] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    const [videoDims, setVideoDims] = useState({ w: 0, h: 0 });
    // Actual display rectangle of the video content (accounts for object-fit:contain)
    const [videoRect, setVideoRect] = useState({ x: 0, y: 0, w: 0, h: 0 });

    // ── Compute the actual display rectangle of the video ──────────
    // object-fit:contain may add letterbox (top/bottom) or pillarbox (left/right).
    // The SVG overlay must be positioned exactly over the video content, not the container.
    const updateVideoRect = useCallback(() => {
      const video = videoRef.current;
      if (!video || !video.videoWidth || !video.videoHeight) return;

      const containerW = video.clientWidth;
      const containerH = video.clientHeight;
      const videoW = video.videoWidth;
      const videoH = video.videoHeight;

      if (!containerW || !containerH) return;

      const containerAspect = containerW / containerH;
      const videoAspect = videoW / videoH;

      let displayW, displayH, offsetX, offsetY;

      if (videoAspect > containerAspect) {
        // Video is wider — letterbox (black bars top/bottom)
        displayW = containerW;
        displayH = containerW / videoAspect;
        offsetX = 0;
        offsetY = (containerH - displayH) / 2;
      } else {
        // Video is taller — pillarbox (black bars left/right)
        displayH = containerH;
        displayW = containerH * videoAspect;
        offsetX = (containerW - displayW) / 2;
        offsetY = 0;
      }

      setVideoRect({
        x: offsetX,
        y: offsetY,
        w: displayW,
        h: displayH,
      });
    }, []);

    // ── Bind stream to <video> safely ───────────────────────────
    useEffect(() => {
      const video = videoRef.current;
      if (!video || !stream) {
        setVideoReady(false);
        return;
      }

      let active = true;

      const updateDimensions = () => {
        if (!active) return;
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        if (vw > 0 && vh > 0) {
          setVideoDims({ w: vw, h: vh });
          setVideoReady(true);
          updateVideoRect();
        }
      };

      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");

      if (video.srcObject !== stream) {
        video.srcObject = stream;
      }

      video.addEventListener("loadedmetadata", updateDimensions);
      video.addEventListener("loadeddata", updateDimensions);
      video.addEventListener("canplay", updateDimensions);
      video.addEventListener("playing", updateDimensions);
      video.addEventListener("timeupdate", updateDimensions);

      // Check immediately if dimensions are already available
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        updateDimensions();
      }

      // Fast polling safety net: on some mobile browsers (Android/iOS), events can fire
      // before videoWidth/videoHeight are populated or event timing is erratic.
      const pollTimer = setInterval(() => {
        if (!active) return;
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          updateDimensions();
          clearInterval(pollTimer);
        }
      }, 100);

      video.play().catch((err) => {
        if (err.name !== "AbortError") {
          console.warn("Video play error in CameraView:", err);
        }
      });

      return () => {
        active = false;
        clearInterval(pollTimer);
        video.removeEventListener("loadedmetadata", updateDimensions);
        video.removeEventListener("loadeddata", updateDimensions);
        video.removeEventListener("canplay", updateDimensions);
        video.removeEventListener("playing", updateDimensions);
        video.removeEventListener("timeupdate", updateDimensions);
        try {
          video.pause();
          video.srcObject = null;
          video.load();
        } catch (e) {}
      };
    }, [stream, updateVideoRect]);

    // Update video rect on resize
    useEffect(() => {
      updateVideoRect();
      window.addEventListener("resize", updateVideoRect);
      return () => window.removeEventListener("resize", updateVideoRect);
    }, [updateVideoRect, videoReady]);

    // ── Capture frame with shutter flash & cooldown ─────────────
    const captureFrame = useCallback(
      (detectedCorners) => {
        const video = videoRef.current;
        if (!video || !video.videoWidth || !video.videoHeight) return;

        // Visual shutter flash feedback
        setIsFlashing(true);
        setTimeout(() => setIsFlashing(false), 220);

        // Arm cooldown to prevent rapid multi-capture on same document
        cooldownUntilRef.current = Date.now() + COOLDOWN_MS;
        lastCapturedCornersRef.current = detectedCorners;
        autoCapturedRef.current = true;

        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0);

        // Sub-pixel corner refinement on full-resolution capture
        let refinedCorners = detectedCorners;
        if (detectedCorners && detectedCorners.length === 4) {
          refinedCorners = detectedCorners.map((c) => refineCornerSubpixel(canvas, c, 14));
        }

        onCapture(canvas, refinedCorners);
      },
      [onCapture]
    );

    // ── Expose capture() for external trigger ───────────────────
    useImperativeHandle(
      ref,
      () => ({
        capture: () => captureFrame(corners),
        getStability: () => stability,
      }),
      [captureFrame, corners, stability]
    );

    // ── Enforce cooldown whenever entering / returning to camera ──
    useEffect(() => {
      if (phase === "camera") {
        cooldownUntilRef.current = Date.now() + COOLDOWN_MS;
        cornerHistoryRef.current = [];
        trackedCornersRef.current = null;
        lastCapturedCornersRef.current = null;
        autoCapturedRef.current = false;
        lastDocumentSeenRef.current = Date.now();
        setCorners(null);
        setStability(0);
        setInCooldown(true);
        setStatusMessage("Point camera at document");
      } else {
        cornerHistoryRef.current = [];
        trackedCornersRef.current = null;
        setCorners(null);
        setStability(0);
        setInCooldown(false);
      }
    }, [phase]);

    // ── Edge detection & stability loop ─────────────────────────
    useEffect(() => {
      // Pause detection when video not ready, processing, or scanner is in review phase
      if (!videoReady || isProcessing || phase !== "camera") return;

      let animId;
      let lastTime = 0;

      const detect = (timestamp) => {
        // Adaptive throttling: slow down when no document found for a while (battery saving)
        const timeSinceDoc = Date.now() - lastDocumentSeenRef.current;
        const interval = timeSinceDoc > IDLE_SLOWDOWN_AFTER ? DETECT_INTERVAL_IDLE : DETECT_INTERVAL;

        if (timestamp - lastTime >= interval) {
          lastTime = timestamp;
          runDetection();
        }
        animId = requestAnimationFrame(detect);
      };

      const runDetection = () => {
        const video = videoRef.current;
        const canvas = detectCanvasRef.current;
        if (
          !video ||
          !canvas ||
          !video.videoWidth ||
          !video.videoHeight
        ) {
          return;
        }

        const now = Date.now();
        const cooldownRemaining = Math.max(0, cooldownUntilRef.current - now);
        const coolingDown = cooldownRemaining > 0;

        // Downsample for fast detection
        const scaleX = DETECT_WIDTH / video.videoWidth;
        const scaleY = scaleX;
        canvas.width = DETECT_WIDTH;
        canvas.height = Math.round(video.videoHeight * scaleY);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const result = detectDocumentEdges(canvas);

        if (result && result.confidence > 0.35) {
          missedFramesRef.current = 0;
          lastDocumentSeenRef.current = now;

          // Scale corners back to full video resolution
          const curScaleX = canvas.width / video.videoWidth;
          const curScaleY = canvas.height / video.videoHeight;
          const scaled = result.corners.map((c) => ({
            x: c.x / curScaleX,
            y: c.y / curScaleY,
          }));

          // Temporal EMA smoothing to eliminate viewfinder jitter
          let smoothed = scaled;
          if (trackedCornersRef.current) {
            const prev = trackedCornersRef.current;
            const maxShift = Math.max(
              ...scaled.map((c, i) => Math.hypot(c.x - prev[i].x, c.y - prev[i].y))
            );
            // If movement is subtle (<8% of screen width), smooth hand tremors
            if (maxShift < video.videoWidth * 0.08) {
              const alpha = 0.45; // Slightly more responsive than before (0.40→0.45)
              smoothed = scaled.map((c, i) => ({
                x: alpha * c.x + (1 - alpha) * prev[i].x,
                y: alpha * c.y + (1 - alpha) * prev[i].y,
              }));
            }
          }
          trackedCornersRef.current = smoothed;
          setCorners(smoothed);

          // If still in the cooldown period: show countdown & block auto-capture
          if (coolingDown) {
            setInCooldown(true);
            const secondsLeft = Math.ceil(cooldownRemaining / 1000);
            setStatusMessage(`Getting ready (${secondsLeft}s)...`);
            setStability(0);
            cornerHistoryRef.current = [];
            return;
          }

          if (inCooldown) {
            setInCooldown(false);
          }

          // Check if user turned page or moved scene after previous capture
          if (lastCapturedCornersRef.current) {
            const diag = Math.hypot(smoothed[2].x - smoothed[0].x, smoothed[2].y - smoothed[0].y) || 1000;
            let moveShift = 0;
            for (let i = 0; i < 4; i++) {
              const d = Math.hypot(
                smoothed[i].x - lastCapturedCornersRef.current[i].x,
                smoothed[i].y - lastCapturedCornersRef.current[i].y
              );
              if (d > moveShift) moveShift = d;
            }
            // Scene changed (page turned or re-aimed > 6% of diagonal)
            if (moveShift / diag > 0.06) {
              lastCapturedCornersRef.current = null;
              autoCapturedRef.current = false;
            }
          }

          // Track stability history
          cornerHistoryRef.current.push(scaled);
          if (cornerHistoryRef.current.length > STABILITY_REQUIRED_FRAMES + 2) {
            cornerHistoryRef.current.shift();
          }
          const stab = calcStability(cornerHistoryRef.current);
          setStability(stab);

          // Update guidance status
          if (!autoCapture) {
            setStatusMessage("Document detected — tap to capture");
          } else if (stab < 0.3) {
            setStatusMessage("Aligning...");
          } else if (stab < 0.85) {
            setStatusMessage("Hold steady...");
          } else {
            setStatusMessage("Capturing...");
          }

          // Auto-capture when document is held steady for STABILITY_REQUIRED_FRAMES
          if (
            autoCapture &&
            stab >= 0.85 &&
            cornerHistoryRef.current.length >= STABILITY_REQUIRED_FRAMES &&
            !autoCapturedRef.current &&
            !coolingDown &&
            !lastCapturedCornersRef.current
          ) {
            autoCapturedRef.current = true;
            const finalSmoothed = getSmoothedCorners(cornerHistoryRef.current) || scaled;
            if (navigator.vibrate) navigator.vibrate(60);
            captureFrame(finalSmoothed);
          }
        } else {
          // Document left frame -> reset page capture lock
          lastCapturedCornersRef.current = null;
          autoCapturedRef.current = false;

          if (coolingDown) {
            setInCooldown(true);
            const secondsLeft = Math.ceil(cooldownRemaining / 1000);
            setStatusMessage(`Getting ready (${secondsLeft}s)...`);
            setCorners(null);
            setStability(0);
            cornerHistoryRef.current = [];
            return;
          }

          if (inCooldown) {
            setInCooldown(false);
          }

          // Allow 1-2 missed frames if we previously had a stable detection track
          if (missedFramesRef.current < 2 && cornerHistoryRef.current.length >= 2) {
            missedFramesRef.current++;
            setStability((prev) => Math.max(0, prev - 0.2));
          } else {
            setCorners(null);
            setStability(0);
            cornerHistoryRef.current = [];
            missedFramesRef.current = 0;
            setStatusMessage("Point camera at document");
          }
        }
      };

      animId = requestAnimationFrame(detect);
      return () => cancelAnimationFrame(animId);
    }, [videoReady, autoCapture, isProcessing, phase, captureFrame, inCooldown]);

    // ── Normalized stability calculation ─────────────────────────
    const calcStability = (history) => {
      if (!history || history.length < 2) return 0;
      const n = history.length;
      const latest = history[n - 1];
      const prev = history[n - 2];

      const docDiagonal =
        Math.hypot(latest[2].x - latest[0].x, latest[2].y - latest[0].y) || 1000;

      let maxShift = 0;
      for (let i = 0; i < 4; i++) {
        const d = Math.hypot(latest[i].x - prev[i].x, latest[i].y - prev[i].y);
        if (d > maxShift) maxShift = d;
      }

      // Drift threshold: < 1.8% of document diagonal is considered motionless
      const relativeDrift = maxShift / docDiagonal;
      const frameStab = Math.max(0, Math.min(1, 1 - relativeDrift / 0.022));
      const progress = Math.min(1, n / STABILITY_REQUIRED_FRAMES);
      return frameStab * progress;
    };

    // ── Temporal corner smoothing on capture ────────────────────
    const getSmoothedCorners = (history) => {
      if (!history || history.length === 0) return null;
      const count = Math.min(history.length, STABILITY_REQUIRED_FRAMES);
      const recent = history.slice(-count);
      const smoothed = [];
      for (let i = 0; i < 4; i++) {
        let sx = 0, sy = 0;
        for (const frame of recent) {
          sx += frame[i].x;
          sy += frame[i].y;
        }
        smoothed.push({ x: sx / count, y: sy / count });
      }
      return smoothed;
    };

    // ── Render ───────────────────────────────────────────────────
    const hasEdges = corners !== null;
    const edgeStrokeW = Math.max(2.5, videoDims.w * 0.004);
    const cornerR = Math.max(8, videoDims.w * 0.012);

    // Edge color transitions: blue when detecting, green when stable/ready
    const edgeColor = stability >= 0.85 ? "#4CAF50" : "#2196F3";
    const edgeFill = stability >= 0.85 ? "rgba(76, 175, 80, 0.10)" : "rgba(33, 150, 243, 0.08)";

    return (
      <div className={`ds-camera-view ${hasEdges ? "ds-detected" : ""}`}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="ds-camera-video"
        />
        <canvas ref={detectCanvasRef} style={{ display: "none" }} />

        {/* Shutter flash effect */}
        {isFlashing && <div className="ds-shutter-flash" />}

        {/* Loading overlay when camera is starting */}
        {!videoReady && !isProcessing && (
          <div className="ds-loading-camera">
            <div className="ds-processing-spinner" />
            <span className="ds-processing-text">Starting camera...</span>
          </div>
        )}

        {/* Adobe Scan-Style Guidance Pill — centered in document area when detected */}
        {videoReady && !isProcessing && (
          <div
            className={`ds-status-pill ${hasEdges ? "ds-pill-detected" : ""} ${stability >= 0.85 ? "ds-pill-ready" : ""}`}
            style={
              hasEdges && corners
                ? {
                    // Position pill at center of detected document (in viewport coords)
                    left: `${videoRect.x + ((corners[0].x + corners[1].x + corners[2].x + corners[3].x) / 4 / videoDims.w) * videoRect.w}px`,
                    top: `${videoRect.y + ((corners[0].y + corners[1].y + corners[2].y + corners[3].y) / 4 / videoDims.h) * videoRect.h}px`,
                    transform: "translate(-50%, -50%)",
                  }
                : undefined
            }
          >
            {autoCapture && !inCooldown && hasEdges && (
              <svg className="ds-pill-progress" viewBox="0 0 36 36">
                <path
                  className="ds-pill-bg"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="ds-pill-fill"
                  strokeDasharray={`${Math.round(stability * 100)}, 100`}
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
            )}
            <span className="ds-pill-text">
              {statusMessage}
            </span>
          </div>
        )}

        {/* Edge detection overlay — FIXED: positioned exactly over the video display area */}
        {hasEdges && videoRect.w > 0 && (
          <svg
            className="ds-edge-overlay"
            viewBox={`0 0 ${videoDims.w} ${videoDims.h}`}
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              left: videoRect.x,
              top: videoRect.y,
              width: videoRect.w,
              height: videoRect.h,
            }}
          >
            {/* Semi-transparent fill inside detected quad */}
            <polygon
              className="ds-edge-polygon"
              points={corners.map((c) => `${c.x},${c.y}`).join(" ")}
              fill={edgeFill}
              stroke={edgeColor}
              strokeWidth={edgeStrokeW}
              strokeLinejoin="round"
            />
            {/* Corner dots — larger, blue like Adobe Scan */}
            {corners.map((c, i) => (
              <circle
                key={i}
                className={`ds-edge-corner ${stability >= 0.85 ? "ds-corner-ready" : ""}`}
                cx={c.x}
                cy={c.y}
                r={cornerR}
                fill={edgeColor}
                stroke="#fff"
                strokeWidth={Math.max(2, edgeStrokeW * 0.6)}
              />
            ))}
          </svg>
        )}

        {/* Guide frame when no edges detected */}
        {!hasEdges && !isProcessing && videoReady && !inCooldown && (
          <div className="ds-guide-frame">
            <div className="ds-corner ds-corner-tl" />
            <div className="ds-corner ds-corner-tr" />
            <div className="ds-corner ds-corner-bl" />
            <div className="ds-corner ds-corner-br" />
          </div>
        )}

        {/* Processing overlay */}
        {isProcessing && (
          <div className="ds-processing-overlay">
            <div className="ds-processing-spinner" />
            <span className="ds-processing-text">Processing scan...</span>
          </div>
        )}
      </div>
    );
  }
);

CameraView.displayName = "CameraView";

export default CameraView;
